import { Uploader } from '@/components/beneficiary/FileUploader'
import { api } from '@/main.tsx'
import { adminStoreActions } from '@/store/actions'
import { Resource } from '@/store/admin'
import { useStoreDispatch } from '@/store/hooks'
import { promptSchema } from '@/validation-schemas.ts'
import { hasError } from '@repo/shared/api/helpers'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Textarea } from '@repo/ui/textarea'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { zCleanString } from '@repo/ui/utils'
import { PlusCircleIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

export function ResourceCreator() {
  const { t, tc } = useT({ keyPrefix: 'resourceCreator' })
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <TooltipWrapper content={tc('createAResource', { cfl: true })}>
        <DialogTrigger asChild>
          <Button variant="default">
            <PlusCircleIcon className="size-4" />
            {tc('create', { cfl: true })}
          </Button>
        </DialogTrigger>
      </TooltipWrapper>

      <DialogContent size="wide" disableOutsideClick>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <ResourceForm onSubmitted={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

export function ResourceScanner() {
  const { t, tc } = useT({ keyPrefix: 'resourceScanner' })
  const [open, setOpen] = useState(false)

  const [resource, setResource] = useState<Pick<
    Resource,
    'content' | 'title'
  > | null>(null)

  useEffect(() => {
    return () => setResource(null)
  }, [open])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <TooltipWrapper content={tc('smartScan', { cfl: true })}>
          <Button variant="highlight" size="icon" onClick={() => setOpen(true)}>
            <IoSparklesSharp className="size-4" />
          </Button>
        </TooltipWrapper>
      </DialogTrigger>
      <DialogContent size={resource ? 'wide' : 'default'} disableOutsideClick>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <Uploader
          // TODO: store file
          isSmart
          processFile={async ({ organizationId, file }) =>
            api.admin.resources.processResource({
              organizationId,
              file,
            })
          }
          onUploaded={(response) => {
            if (hasError(response)) return
            setResource({ title: response.title, content: response.summary })
          }}
        />

        {resource && (
          <ResourceForm
            newValues={resource}
            onSubmitted={() => setOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export function ResourceForm({
  editableResource,
  newValues,
  onSubmitted,
}: {
  editableResource?: Resource
  newValues?: Pick<Resource, 'content' | 'title'>
  onSubmitted: () => void
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT({ keyPrefix: 'resourceCreator' })

  const defaultValues = useMemo(
    () => ({
      title: editableResource?.title || '',
      content: editableResource?.content || '',
    }),
    [editableResource],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } =
    useFormValidator({
      defaultValues,
      onSubmit: ({ title, content }) => {
        if (editableResource) {
          void dispatch(
            adminStoreActions.resource.update({
              resourceId: editableResource.id,
              fields: {
                content,
                title,
              },
            }),
          )
        } else {
          void dispatch(adminStoreActions.resource.create({ title, content }))
          form.reset(defaultValues)
        }
        onSubmitted()
      },
      schema: {
        title: zCleanString.min(3).max(500),
        content: promptSchema,
      },
    })

  useEffect(() => {
    if (newValues) form.reset(newValues)
  }, [newValues, form])

  return (
    <Form {...form}>
      <form
        onSubmit={submit}
        className="space-y-2"
        onKeyDown={submitOnPressMetaEnter}
      >
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Textarea
                  id="title"
                  placeholder="Resource's Title"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <Textarea
                  id="content"
                  placeholder="Resource's Content"
                  className="min-h-96 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="submit" disabled={!isFormValid}>
            {tc(editableResource ? 'update' : 'create', { cfl: true })}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
